<template>
  <div>
    <template v-for="(item, index) in sortedItems" :key="'group-' + index">
      <div class="">
        <PlacementAnchor
          :is-dragging="isDragging"
          @drop.prevent="
            () => drop(sortedItems[index - 1] ?? null, item ?? null)
          "
        />

        <slot
          :key="'item-slot-' + index"
          :item="item"
          :index="index"
          name="item"
          :on-drag-start="() => onDragStart(item)"
          :on-drag-end="onDragEnd"
        />

        <PlacementAnchor
          :is-dragging="isDragging"
          @drop.prevent="
            () => drop(item ?? null, sortedItems[index + 1] ?? null)
          "
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed, ref } from 'vue'
import { generateKeyBetween } from 'fractional-indexing'
import PlacementAnchor from './PlacementAnchor.vue'

type Item = T & { order: string | null; id: string | number }

const props = defineProps<{
  items: Item[]
}>()

const isDragging = ref(false)
const draggedItem = ref<Item | null>(null)

const emit = defineEmits<{
  (e: 'item:moved', d: { item: Item; fractionalIndex: string }): void
}>()

const sortedItems = computed(() =>
  props.items.toSorted((a, b) => {
    if (a.order === null && b.order === null) return 0
    if (a.order === null) return -1
    if (b.order === null) return 1
    return a.order > b.order ? 1 : -1
  }),
)

function onDragStart(item: Item) {
  draggedItem.value = item
  setTimeout(() => {
    isDragging.value = true
  }, 0)
}

function onDragEnd() {
  isDragging.value = false
  draggedItem.value = null
}

function drop(before: Item | null, after: Item | null) {
  if (!draggedItem.value) return
  if (before?.id === draggedItem.value.id || after?.id === draggedItem.value.id)
    return

  const fractionalIndex = generateKeyBetween(before?.order, after?.order)
  emit('item:moved', { item: draggedItem.value, fractionalIndex })
}
</script>
